import { ChevronDownIcon } from '@chakra-ui/icons'
import type {
  GridProps} from '@chakra-ui/react'
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useDisclosure,
  useToken,
} from '@chakra-ui/react'
import { Ellipsis } from 'BrokerTsLib/src/components'
import type { AppInfoMessage } from 'BrokerTsLib/src/dtos'
import { useState } from 'react'

import { formatDate } from '../../utils/formatDate'

interface NewsItemProps extends AppInfoMessage {
  onSetRead: (id: number) => void
  first?: boolean
  last?: boolean
}

export const NewsItem = (props: NewsItemProps) => {
  const {
    messageHeader,
    messageDate,
    messageImportance,
    read,
    onSetRead,
    messageText,
    id,
    first,
    last,
    type,
  } = props
  const { isOpen, onToggle } = useDisclosure()
  const [isEllipsisActive, setIsEllipsisActive] = useState(false)

  const [infoBg, infoFg, warnBg, warnFg, critBg, critFg] = useToken('colors', [
    'news-timeline-info-bg',
    'news-timeline-info-fg',
    'news-timeline-warn-bg',
    'news-timeline-warn-fg',
    'news-timeline-crit-bg',
    'news-timeline-crit-fg',
  ])

  const [fg, bg] =
    messageImportance === 3
      ? [critFg, critBg]
      : messageImportance === 2
        ? [warnFg, warnBg]
        : [infoFg, infoBg]

  return (
    <NewsItemGrid role="group">
      <TimelineItemGrid
        onClick={onToggle}
        cursor={isEllipsisActive ? 'pointer' : 'default'}
        color={fg}
        bg={bg}
        first={first}
        last={last}
      >
        {messageDate ? formatDate(messageDate, 'd. M.') : ''}
      </TimelineItemGrid>

      <Grid sx={{ gap: 2, alignItems: 'center' }} role="group">
        <Box>
          <Flex sx={{ alignItems: 'center', gap: 2 }}>
            {messageHeader && (
              <Text sx={{ fontWeight: 'bold' }}>{messageHeader}</Text>
            )}
            {type && <Badge colorScheme="blue">{type}</Badge>}
            {!read && <UnreadDot />}
            {!read && (
              <Button
                size="xs"
                sx={{ display: 'none' }}
                _groupHover={{ display: 'block' }}
                variant={'link'}
                onClick={() => onSetRead(id)}
              >
                Označit za přečtené
              </Button>
            )}
          </Flex>
          {messageDate && (
            <Text fontSize="sm" fontWeight="light">
              {formatDate(messageDate)}
            </Text>
          )}
          <Ellipsis
            disableTooltip
            fontWeight={read ? 'normal' : 'semibold'}
            noOfLines={isOpen ? Infinity : 2}
            dangerouslySetInnerHTML={{ __html: messageText ?? '' }}
            onEllipsisActiveChange={setIsEllipsisActive}
          ></Ellipsis>
        </Box>
      </Grid>

      {isEllipsisActive && (
        <Button
          size="lg"
          sx={{
            visibility: isOpen ? 'visible' : 'hidden',
            _groupHover: { visibility: 'visible' },
            h: 'full',
            px: 3,
          }}
          variant="ghost"
          onClick={onToggle}
        >
          <ChevronDownIcon
            boxSize={6}
            transform={isOpen ? 'rotate(180deg)' : 'none'}
          />
        </Button>
      )}
    </NewsItemGrid>
  )
}

const UnreadDot = () => {
  const dotSize = '6px'

  return (
    <Box
      title="Nepřečteno"
      sx={{
        display: 'inline-block',
        borderRadius: dotSize,
        width: dotSize,
        height: dotSize,
        backgroundColor: 'primary.700',
      }}
    />
  )
}

export const NewsItemGrid = (props: GridProps) => {
  return (
    <Grid
      {...props}
      sx={{
        pb: 2,
        py: 4,
        gap: 6,
        alignContent: 'start',
        alignItems: 'start',
        gridTemplateColumns: '2.5rem 1fr 2rem',
        fontSize: 'sm',
      }}
    ></Grid>
  )
}

export interface TimelineItemGridProps extends GridProps {
  first?: boolean
  last?: boolean
}

export const TimelineItemGrid = ({
  children,
  color,
  border,
  borderColor,
  bg,
  fontWeight,
  fontSize,
  first,
  last,
  ...rest
}: TimelineItemGridProps) => (
  <Grid
    sx={{
      h: '100%',
      gridTemplateRows: '0 2.25rem 1fr',
      justifyItems: 'center',
    }}
    {...rest}
  >
    <Box
      sx={{
        mt: -4,
        h: 4,
        bg,
        visibility: first ? 'hidden' : 'visible',
        borderLeft: border ?? '1px solid',
        borderColor: borderColor ?? bg,
      }}
    />
    <Box
      sx={{
        w: 9,
        h: 9,
        borderRadius: 'full',
        bg,
        alignContent: 'center',
        textAlign: 'center',
        color: color,
        fontSize: fontSize ?? 'xs',
        fontWeight: fontWeight ?? 'bold',
        border,
        borderColor,
      }}
    >
      {children}
    </Box>

    <Box
      sx={{
        h: 'calc(100% + 1rem)',
        bg,
        visibility: last ? 'hidden' : 'visible',
        borderLeft: border ?? '1px solid',
        borderColor: borderColor ?? bg,
      }}
    />
  </Grid>
)
